import { Injectable, inject } from '@angular/core';
import { ENV_CONFIG, EnvironmentConfig, IAttachmentModel } from '@libs/vc-core-lib';
import { map, Observable } from 'rxjs';
import { ITaskModel } from '../model/task.model';
import { ITaskDocumentPatchRequestModel } from '../model/task-document-patch-request.model';
import { ITaskUsersBulkPatchRequest, ITaskUsersPatchRequest } from '../model/task-access-patch-request.model';
import { ITaskDetailPatchRequestModel } from '../model/task-detail-patch-request.model';
import { TransitionAwareTaskStatus } from '../model/transition-aware-task-status.model';
import { ITaskAccessBulkPatchRequestModel } from '../model/task-access-bulk-patch-request.model';
import { ITaskStatusBulkPatchRequestModel } from '../model/task-status-bulk-patch-request.model';
import {
    ITaskBulkPatchFullResultModel,
    ITaskBulkPatchFullResultResponse,
} from '../model/task-bulk-patch-full-result.model';
import { IDocLinkPatchRequestModel } from '../model/doc-link-patch-request.model';
import { HttpClient } from '@angular/common/http';
import { IBulkTaskDueDateModel } from '../model/bulk-task-due-date.model';

@Injectable({
    providedIn: 'root',
})
export class TaskService {
    private readonly _httpClient: HttpClient = inject(HttpClient);
    private readonly _environment: EnvironmentConfig = inject(ENV_CONFIG);
    private static _selectedTaskStatusBulkPatchRequest: ITaskStatusBulkPatchRequestModel;
    private static _selectedTaskBulkPatchFullResult: ITaskBulkPatchFullResultModel;
    private static _selectedActionValue: string;

    public patchTaskClients(
        taskId: number,
        assessmentId: number,
        patchRequest: ITaskUsersPatchRequest
    ): Observable<ITaskModel> {
        return this._httpClient.patch<ITaskModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/task/${taskId}/clients`,
            patchRequest
        );
    }

    public patchTaskAssessors(
        taskId: number,
        assessmentId: number,
        patchRequest: ITaskUsersPatchRequest
    ): Observable<ITaskModel> {
        return this._httpClient.patch<ITaskModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/task/${taskId}/assessors`,
            patchRequest
        );
    }

    public patchTaskDocuments(
        taskId: number,
        assessmentId: number,
        patchRequest: ITaskDocumentPatchRequestModel
    ): Observable<ITaskModel> {
        return this._httpClient.patch<ITaskModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/task/${taskId}/documents`,
            patchRequest
        );
    }

    public patchTaskDetail(
        taskId: number,
        assessmentId: number,
        patchRequest: ITaskDetailPatchRequestModel
    ): Observable<ITaskModel> {
        return this._httpClient.patch<ITaskModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/task/${taskId}/detail`,
            patchRequest
        );
    }

    public getAvailableTaskStateTransition(
        taskId: number,
        assessmentId: number
    ): Observable<Array<TransitionAwareTaskStatus>> {
        return this._httpClient.get<Array<TransitionAwareTaskStatus>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/task/${taskId}/status-transitions`
        );
    }

    public addClientsToBulkTasks(
        assessmentId: number,
        taskAccessBulkPatchRequest: ITaskUsersBulkPatchRequest
    ): Observable<ITaskModel[]> {
        return this._httpClient.patch<Array<ITaskModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/tasks/add-clients`,
            {
                assignedTaskUsers: taskAccessBulkPatchRequest.assignedTaskUsers,
                idVersionMap: Object.fromEntries(taskAccessBulkPatchRequest.idVersionMap),
                updateThisOnly: taskAccessBulkPatchRequest.updateThisOnly,
            }
        );
    }

    public removeClientsFromBulkTasks(
        assessmentId: number,
        taskAccessBulkPatchRequest: ITaskUsersBulkPatchRequest
    ): Observable<ITaskModel[]> {
        return this._httpClient.patch<Array<ITaskModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/tasks/remove-clients`,
            {
                assignedTaskUsers: taskAccessBulkPatchRequest.assignedTaskUsers,
                idVersionMap: Object.fromEntries(taskAccessBulkPatchRequest.idVersionMap),
            }
        );
    }

    public revokeClientsOwnershipFromTasks(
        assessmentId: number,
        taskAccessBulkPatchRequest: ITaskAccessBulkPatchRequestModel
    ): Observable<ITaskModel[]> {
        return this._httpClient.patch<Array<ITaskModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/tasks/revoke-ownership`,
            {
                assignedTaskUsers: taskAccessBulkPatchRequest.assignedTaskUsers,
                idVersionMap: Object.fromEntries(taskAccessBulkPatchRequest.idVersionMap),
            }
        );
    }

    public addAssessorsToBulkTasks(
        assessmentId: number,
        taskAccessBulkPatchRequest: ITaskAccessBulkPatchRequestModel
    ): Observable<ITaskModel[]> {
        return this._httpClient.patch<Array<ITaskModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/tasks/add-assessors`,
            {
                assignedTaskUsers: taskAccessBulkPatchRequest.assignedTaskUsers,
                idVersionMap: Object.fromEntries(taskAccessBulkPatchRequest.idVersionMap),
            }
        );
    }

    public removeAssessorsFromBulkTasks(
        assessmentId: number,
        taskAccessBulkPatchRequest: ITaskAccessBulkPatchRequestModel
    ): Observable<ITaskModel[]> {
        return this._httpClient.patch<Array<ITaskModel>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/tasks/remove-assessors`,
            {
                assignedTaskUsers: taskAccessBulkPatchRequest.assignedTaskUsers,
                idVersionMap: Object.fromEntries(taskAccessBulkPatchRequest.idVersionMap),
            }
        );
    }

    public patchTaskStatusForTasks(
        assessmentId: number,
        taskStatusBulkPatchRequest: ITaskStatusBulkPatchRequestModel
    ): Observable<ITaskBulkPatchFullResultModel> {
        return this._httpClient
            .patch<ITaskBulkPatchFullResultResponse>(
                `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/tasks/status`,
                {
                    ...taskStatusBulkPatchRequest,
                    idVersionMap: Object.fromEntries(taskStatusBulkPatchRequest.idVersionMap),
                }
            )
            .pipe(
                map((response: ITaskBulkPatchFullResultResponse) => {
                    return {
                        success: response.success,
                        failure: response.failure,
                        idErrorMap: new Map(
                            Object.entries(response.idErrorMap).map(([key, value]) => [Number(key), value])
                        ),
                    };
                })
            );
    }

    public getSelectedTaskStatusBulkPatchRequest(): ITaskStatusBulkPatchRequestModel {
        return TaskService._selectedTaskStatusBulkPatchRequest;
    }

    public setSelectedTaskStatusBulkPatchRequest(taskStatusBulkPatchRequest: ITaskStatusBulkPatchRequestModel): void {
        TaskService._selectedTaskStatusBulkPatchRequest = taskStatusBulkPatchRequest;
    }

    public getSelectedTaskBulkPatchFullResult(): ITaskBulkPatchFullResultModel {
        return TaskService._selectedTaskBulkPatchFullResult;
    }

    public setSelectedTaskBulkPatchFullResult(taskBulkPatchFullResult: ITaskBulkPatchFullResultModel): void {
        TaskService._selectedTaskBulkPatchFullResult = taskBulkPatchFullResult;
    }

    public setSelectedActionValue(actionValue: string): void {
        TaskService._selectedActionValue = actionValue;
    }

    public getSelectedActionValue(): string {
        return TaskService._selectedActionValue;
    }

    public syncFileAssociations(assessmentId: number, taskIds: string[] | number[]): Observable<void> {
        return this._httpClient.patch<void>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/tasks/sync-file-associations`,
            taskIds
        );
    }

    public patchTaskDocLinks(
        taskId: number,
        assessmentId: number,
        patchRequest: IDocLinkPatchRequestModel
    ): Observable<ITaskModel> {
        return this._httpClient.patch<ITaskModel>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/task/${taskId}/doc-links`,
            patchRequest
        );
    }

    public patchBulkTasksDocuments(
        assessmentId: number,
        taskIdDocMap: Record<string, IAttachmentModel[]>
    ): Observable<void> {
        return this._httpClient.patch<void>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/tasks/documents`,
            taskIdDocMap
        );
    }

    public getNamesByTaskFolderPaths(paths: string[]): Observable<Map<string, string>> {
        return this._httpClient.post<Map<string, string>>(
            `${this._environment.APP.VC_ASSESSMENT_BASE_URL}/tasks/names`,
            paths
        );
    }

    public updateBulkTaskDueDate(
        assessmentId: number,
        bulkTaskDueDateModel: IBulkTaskDueDateModel
    ): Observable<Map<number, string>> {
        return this._httpClient
            .post<
                Record<number, string>
            >(`${this._environment.APP.VC_ASSESSMENT_BASE_URL}/assessment/${assessmentId}/bulk-task-due-date`, bulkTaskDueDateModel)
            .pipe(
                map(
                    (taskObj: Record<number, string>): Map<number, string> =>
                        new Map(Object.entries(taskObj).map(([key, value]) => [Number(key), value] as [number, string]))
                )
            );
    }
}
